import { ACCOUNTS } from "@/data/mock-data";
import type { BrokerAccount } from "@/data/mock-data";

/**
 * Backtest-chart adapter (Sergey 2026-05-21).
 *
 * `PortfolioVsSpyChart` is account-coupled but only reads ten fields
 * off the `BrokerAccount` it's handed (curveDates, equityCurve,
 * twrCurve, spyCurve, spyTotalCurve, cashflows, targetApyPct,
 * portfolioBasis, allTimeChangePct, id). To reuse the full account
 * chart — range presets, $/% toggle, SPY comparison, target line — for
 * a bot's *backtest* curve we synthesise a `BrokerAccount` carrying the
 * simulated curve and spread a real account for the unused remainder.
 */

/**
 * Deterministic backtest equity curve. Seeded by `seed` so a given bot
 * always draws the same shape, climbing from a $10k base to a total
 * return of `totalReturnPct` over `points` daily samples.
 */
export function syntheticBacktestCurve(
  seed: string,
  totalReturnPct: number,
  points = 365
): number[] {
  // Tiny seeded LCG so the curve is stable across renders/sessions.
  let h = 2166136261;
  for (let i = 0; i < seed.length; i++) {
    h = (h ^ seed.charCodeAt(i)) >>> 0;
    h = (h * 16777619) >>> 0;
  }
  const rand = () => {
    h = (h * 1103515245 + 12345) & 0x7fffffff;
    return h / 0x7fffffff;
  };

  const start = 10000;
  const end = start * (1 + totalReturnPct / 100);
  const n = Math.max(2, Math.floor(points));
  const out: number[] = [];
  for (let i = 0; i < n; i++) {
    const t = i / (n - 1);
    // Smooth start→end trend plus bounded deterministic noise that
    // grows slightly over time so the curve looks like a real walk.
    const trend = start + (end - start) * t;
    const noise = (rand() - 0.5) * start * 0.07 * (0.35 + t);
    out.push(Math.max(start * 0.4, trend + noise));
  }
  // Pin the endpoints so the total return matches exactly.
  out[0] = start;
  out[n - 1] = end;
  return out;
}

/**
 * Wrap an equity curve in a synthetic `BrokerAccount` so it can be fed
 * straight to `PortfolioVsSpyChart`. No cashflows (a backtest has none),
 * so `portfolioBasis` is `"total-equity"` and TWR is the plain return
 * index off day 0.
 */
export function buildBacktestAccount(
  id: string,
  equityCurve: number[]
): BrokerAccount {
  const n = equityCurve.length;
  const today = new Date();
  const curveDates = equityCurve.map((_, i) => {
    const d = new Date(today.getTime() - (n - 1 - i) * 86400e3);
    return d.toISOString().slice(0, 10);
  });
  const base = equityCurve[0] || 1;
  const twrCurve = equityCurve.map((v) => (v / base - 1) * 100);
  // Deterministic SPY benchmark — a gentle drift so the comparison
  // lines have something to plot against. Both series are rebased to
  // 0% at the window start, matching the real-account contract.
  const spyCurve = equityCurve.map((_, i) => i * 0.028);
  const spyTotalCurve = equityCurve.map((_, i) => i * 0.042);

  return {
    ...ACCOUNTS[0]!,
    id: `backtest-${id}`,
    curveDates,
    equityCurve,
    twrCurve,
    spyCurve,
    spyTotalCurve,
    cashflows: [],
    targetApyPct: 20,
    portfolioBasis: "total-equity",
    allTimeChangePct: (twrCurve[n - 1] ?? 0) / 100
  };
}
